import type { Resource, ResourceType } from '@medplum/fhirtypes'
import {
  type Icon,
  IconCalendarClock,
  IconCalendarEvent,
  IconCash,
  IconChecklist,
  IconClipboardHeart,
  IconClipboardList,
  IconClipboardText,
  IconFileText,
  IconFlask,
  IconHeartbeat,
  IconMail,
  IconMedicalCross,
  IconMessage,
  IconMicroscope,
  IconPill,
  IconReportMedical,
  IconShieldHeart,
  IconStethoscope,
  IconUser,
  IconUsers,
  IconVaccine,
  IconWheelchair,
} from '@tabler/icons-react'
import type { ComponentProps, JSX } from 'react'
import { IconOrder } from '#src/components/icons/IconOrder.tsx'

// General-purpose FHIR resource icon. Feed it a full `resource`, a bare
// `resourceType`, or a `reference` string (`ResourceType/id`). It resolves a
// sensible default per resourceType and, when given the full resource, refines
// to a more specific icon if one can be identified (e.g. an imaging
// ServiceRequest → microscope).

// Default icon per resourceType. Partial — unmapped types fall back to FALLBACK_ICON.
const DEFAULT_ICONS: Partial<Record<ResourceType, Icon>> = {
  Coverage: IconShieldHeart,
  Appointment: IconCalendarEvent,
  ServiceRequest: IconOrder,
  Task: IconChecklist,
  Patient: IconUser,
  MedicationRequest: IconPill,
  Immunization: IconVaccine,
  Condition: IconClipboardHeart,
  Observation: IconHeartbeat,
  DiagnosticReport: IconReportMedical,
  Encounter: IconStethoscope,
  CarePlan: IconClipboardList,
  CareTeam: IconUsers,
  Claim: IconCash,
  Communication: IconMessage,
  CommunicationRequest: IconMail,
  DocumentReference: IconFileText,
}

export const FALLBACK_ICON: Icon = IconFileText

// Per-resourceType refiners: given the full resource, return a more specific
// icon, or undefined to keep the default. Only consulted when a full resource
// (not just a type/reference) is available.
const REFINERS: Partial<
  Record<ResourceType, (resource: Resource) => Icon | undefined>
> = {
  ServiceRequest: (r) => {
    const codes = collectCodings(
      (r as { category?: unknown[] }).category as
        | { coding?: { code?: string; display?: string }[] }[]
        | undefined
    )
    if (codes.some((c) => /imag|radiolog/i.test(c))) {
      return IconMicroscope
    }
    if (codes.some((c) => /procedure|surg/i.test(c))) {
      return IconMedicalCross
    }
    if (codes.some((c) => /referral|consult/i.test(c))) {
      return IconStethoscope
    }
    if (codes.some((c) => /lab|specimen/i.test(c))) {
      return IconFlask
    }
    if (codes.some((c) => /dme/i.test(c))) {
      return IconWheelchair
    }
    return undefined
  },
  Task: (r) => {
    const code = collectCodings(
      (r as { code?: { coding?: { code?: string; display?: string }[] } }).code
        ? [
            (r as { code: object }).code as {
              coding?: { code?: string; display?: string }[]
            },
          ]
        : undefined
    )
    const text = [
      code.join(' '),
      (r as { description?: string }).description ?? '',
    ]
      .join(' ')
      .toLowerCase()
    if (/schedul|appointment|transport/.test(text)) {
      return IconCalendarClock
    }
    if (/document|file|note|chart/.test(text)) {
      return IconClipboardText
    }
    return undefined
  },
}

export type ResourceIconProps = Omit<ComponentProps<Icon>, 'ref'> &
  (
    | { resource: Resource; resourceType?: never; reference?: never }
    | { resourceType: string; resource?: never; reference?: never }
    | { reference: string; resource?: never; resourceType?: never }
  )

export function ResourceIcon({
  resource,
  resourceType,
  reference,
  ...iconProps
}: ResourceIconProps): JSX.Element {
  const Icon = resolveResourceIcon({ resource, resourceType, reference })
  return <Icon {...iconProps} />
}

// Resolve the icon constructor for a resource / type / reference.
function resolveResourceIcon(input: {
  resource?: Resource
  resourceType?: string
  reference?: string
}): Icon {
  const type =
    input.resource?.resourceType ??
    input.resourceType ??
    (input.reference ? input.reference.split('/')[0] : undefined)

  if (!type) {
    return FALLBACK_ICON
  }

  const rt = type as ResourceType
  if (input.resource) {
    const refined = REFINERS[rt]?.(input.resource)
    if (refined) {
      return refined
    }
  }
  return DEFAULT_ICONS[rt] ?? FALLBACK_ICON
}

// Flatten codings/categories into a list of code + display strings for matching.
function collectCodings(
  entries: { coding?: { code?: string; display?: string }[] }[] | undefined
): string[] {
  if (!entries) {
    return []
  }
  const out: string[] = []
  for (const entry of entries) {
    for (const coding of entry.coding ?? []) {
      if (coding.code) {
        out.push(coding.code)
      }
      if (coding.display) {
        out.push(coding.display)
      }
    }
  }
  return out
}
